import React, { useState, useEffect, useMemo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { CmsComponentProps } from '@/types/cms';
import { getDeliveryDate } from '@/utils/common/porder';
import { getHomePageDataByUrl } from '@/api/content';
import useTranslation from 'next-translate/useTranslation';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import ProductCard from '@/components/common/ProductCard/VerticalItemLine';
import { AddToCartReferType } from '@/constants/track';
import More from '../Layout/more';
import Header from '../Layout/header';
import Container from '../Layout/container';
import LazyView from '@/components/common/LazyLoad/view-lazy';
import ProductCardSkeleton from '@/components/common/Skeleton/new-product-card';
import { useComponentTheme } from '../../hooks/useComponentTheme';
import { getConfigBg } from '@/components/component-pool/tools/config-bg';
import useAccount from '@/store/account';
import { filterMaxPurchasedProducts } from '@/utils/productFormat';
import { relativeLinkIfQAEnv } from '../../tools/cms-link';
import { isLatinoChannel } from '@/utils/common/latino-app';

const ItemLine: React.FC<CmsComponentProps> = props => {
  const {
    data,
    cmsComponentKey,
    properties,
    transformProductLink,
    postUrl,
    skeleton,
    showSoldBy = true,
    componentPos,
    trackingContext,
    customAddCartApi,
    addCartParams,
    pageTheme = null,
    showFavorite = false,
    showPantryIcon = true
  } = props;

  const { lang } = useTranslation('common');
  const hasInitialData = !!data;
  const [load, setLoad] = useState(!hasInitialData);
  const [products, setProducts] = useState<ProductType[]>(data?.products || []);
  const [comonentMetaData, setcomonentMetaData] = useState<ObjectType>(data?.component_metadata || {});
  const { titleStyle, moreLinkStyle } = useComponentTheme({ properties, pageTheme });
  const eventKey = properties?.event_key || cmsComponentKey;
  const isLatino = isLatinoChannel();

  const { userBoughtData } = useAccount();

  const bgStyle = getConfigBg(properties?.background);

  const fetchData = async () => {
    const res = await getHomePageDataByUrl(postUrl, {
      lang,
      dataobject_key: props?.componentDataSourceKey
    });
    if (res?.result) {
      setLoad(false);

      setProducts(
        props?.filterMaxPurchasedCard
          ? filterMaxPurchasedProducts(res?.object?.products, userBoughtData)
          : res?.object?.products
      );
      setcomonentMetaData(res?.object?.component_metadata || {});
    }
  };

  useEffect(() => {
    if (hasInitialData) return;
    fetchData();
  }, [hasInitialData]);

  const notMaxLimitProducts = useMemo(() => {
    return props?.filterMaxPurchasedCard ? filterMaxPurchasedProducts(products, userBoughtData) : products;
  }, [userBoughtData, products, props?.filterMaxPurchasedCard]);

  if (load) {
    return skeleton;
  }

  if (!load && !notMaxLimitProducts?.length) return null;

  const title = comonentMetaData?.title || properties.title;

  return (
    <Container
      cmsComponentKey={props.cmsComponentKey}
      className="overflow-hidden"
      style={{ ...bgStyle }}
      testId={`mod-item-carousel-${(title ?? 'no-title').replace(/\s+/g, '-')}`}>
      {/* header */}
      <Header
        cmsComponentKey={props.cmsComponentKey}
        title={title}
        moreLink={comonentMetaData?.more_link || properties.more_link}
        moreLinkStyle={moreLinkStyle}
        subTitle={comonentMetaData?.sub_title || properties.sub_title}
        titleStyle={titleStyle}
        trackingParams={{
          mod_nm: eventKey,
          mod_pos: `${componentPos}`,
          ctx: trackingContext
        }}
        testId={'btn-more-link'}
      />

      {/* product */}
      <Swiper
        role="list"
        slidesPerView="auto"
        freeMode
        spaceBetween={4}
        slidesOffsetAfter={10}
        slidesOffsetBefore={10}
        className="my-100 items-stretch">
        {notMaxLimitProducts?.map((item, index) => {
          return (
            <SwiperSlide
              role="listitem"
              key={`${item.id}-${index}`}
              className={twMerge('flex-none !h-auto flex items-stretch', isLatino ? '!w-[130px]' : '!w-[150px]')}>
              <LazyView
                rootMargin="6px 0px 0px 6px"
                skeleton={
                  index > 2 && <ProductCardSkeleton className={twJoin(isLatino ? '!w-[130px]' : '!w-[150px]')} />
                }>
                <ProductCard
                  transformLink={transformProductLink}
                  isShow3Lines={isLatino ? false : true}
                  addCartParams={{
                    referType: AddToCartReferType.normal,
                    deliveryDate: getDeliveryDate(),
                    ...((addCartParams || {}) as any)
                  }}
                  positionInfoT2={{
                    modSecPos: {
                      mod_nm: eventKey,
                      mod_pos: componentPos
                    },
                    prodPos: index,
                    context: {
                      tag_key: item?.entrance_tag?.tag_key,
                      tag_name: item?.entrance_tag?.tag_name,
                      global_vendor: item?.biz_type === 'seller' ? item?.vender_id : null,
                      ...trackingContext
                    }
                  }}
                  showFavorite={showFavorite}
                  showWeekly
                  showSoldBy={showSoldBy}
                  data={item}
                  showPantryIcon={showPantryIcon}
                  customAddCartApi={customAddCartApi}
                />
              </LazyView>
            </SwiperSlide>
          );
        })}
        {!!properties?.more_link && customAddCartApi !== 'mkplGroupOrder' && (
          <SwiperSlide
            className={twMerge('!h-auto flex items-center justify-center', isLatino ? '!w-[130px]' : '!w-[150px]')}>
            <More
              moreLink={relativeLinkIfQAEnv(properties?.more_link)}
              trackingParams={{
                mod_nm: eventKey,
                mod_pos: `${componentPos}`,
                ctx: trackingContext
              }}
            />
          </SwiperSlide>
        )}
      </Swiper>
    </Container>
  );
};

export default ItemLine;
